import React, { createContext, useContext, useCallback, useMemo } from "react";
import { Updater } from "use-immer";
import {
  type DocumentElement,
  type EacoDocumentContent,
  type LogicRule,
  type ResolvedElementState,
  resolveLogic,
  valueAtPath,
  setValueAtPath,
  removeAtPath,
  type ValuePath,
} from "@eaco/forms-core";

import { useConfigFormSubject } from "./ConfigFormSubjectContext";
import type { PastedBlock } from "../elements/pasteBlocks";

/** A block type a `/` slash command can convert the current block into. */
export interface SlashCommandType {
  typeId: string;
  name: string;
  /** FontAwesome regular icon name (rendered via the app's global library). */
  icon: string;
}

/** Direction of a caret arrow that ran off the edge of its block. */
export type ArrowDirection = "up" | "down" | "left" | "right";

/** Where to drop the caret when a block is auto-focused by arrow navigation. */
export interface AutoFocusCoords {
  /** Screen x to match on the target's first/last line. */
  x: number;
  edge: "first" | "last";
}

/**
 * A character-granular cross-block text selection, normalised to document
 * order. Positions are ProseMirror positions inside each endpoint block's own
 * editor. Blocks listed in `coveredIds` sit strictly between the endpoints
 * and are fully selected.
 */
export interface VirtualTextSelection {
  startId: string;
  startPos: number;
  endId: string;
  endPos: number;
  coveredIds: readonly string[];
}

interface DocumentContextValue {
  document: EacoDocumentContent;
  isEditor: boolean;
  /**
   * True when the document is rendered purely for viewing — fields
   * should hide their action chrome (Edit, Clear, Delete row, etc.)
   * and any autocomplete inputs. Field values can still render; just
   * the controls that would let the user mutate them should disappear.
   */
  isReadOnly: boolean;
  /**
   * True when rendering for PDF/print (server-side gotenberg pipeline).
   * Renderers that draw an on-screen "paper"/page metaphor (e.g. the Pages
   * viewer cards) should instead treat pages as hard page breaks and drop the
   * card chrome, so the printed A4 output isn't a screenshot of the preview.
   * Implies isReadOnly.
   */
  isPrint: boolean;
  /**
   * Read-only preview of a BLANK form (template preview, remix studio):
   * inputs show their configured defaults, as the filler would see them.
   * Off by default so a read-only response view shows persisted answers
   * only and never passes a default off as something the filler entered.
   */
  previewDefaults: boolean;
  /** The full root values object */
  values: Record<string, unknown>;
  updateValues: Updater<Record<string, unknown>>;
  /**
   * Read a value at a path (relative to root). For non-repeat fields the path
   * is just `[elementId]`. For repeat scopes it's threaded by RepeatScopeProvider.
   */
  getAtPath: (path: ValuePath) => unknown;
  /** Write a value at a path. */
  setAtPath: (path: ValuePath, value: unknown) => void;
  /** Remove the entry at a path (object delete or array splice). */
  removeAtPath: (path: ValuePath) => void;
  /**
   * Optional per-element annotation slot. When provided, the dispatcher
   * renders its return INLINE under each leaf input element (not under
   * structural containers). Used by the sign-off review to put reviewer
   * comments next to each answer. Returns null/undefined to render
   * nothing for a given element.
   */
  renderElementAnnotation?: (elementId: string) => React.ReactNode;
  /**
   * Authoring write-path. Only provided in editor mode (and only by hosts that
   * support self-editing blocks, e.g. the page-document editor). Lets a leaf
   * element mutate its OWN `data` in the document content — as opposed to
   * `values`, which holds fill answers. Absent → the element renders read-only.
   */
  updateElementData?: (
    elementId: string,
    updater: (data: Record<string, unknown>) => void,
  ) => void;
  /**
   * Notion-style Enter handling. A self-editing block calls this when the user
   * presses Enter (`isEmpty` = whether the block's content is empty). The host
   * (page-document editor) decides what happens: insert the element type's
   * `nextElement` block after it, or convert an empty block to a spacer.
   * `atStart` = the caret was at the very start of a non-empty block — the host
   * inserts an empty plain-text block ABOVE instead, leaving this block (and
   * the caret) untouched. Absent in view/fill mode and in the form editor.
   */
  onElementEnter?: (
    elementId: string,
    opts: { isEmpty: boolean; afterHtml?: string; atStart?: boolean },
  ) => void;
  /**
   * Notion-style Backspace at the very start of a block. A text-based block
   * calls this passing its current content; the host converts a non-text block
   * to plain text, or merges a text block into the nearest preceding text-based
   * block. Absent in view/fill and in the form editor.
   */
  onElementBackspaceStart?: (
    elementId: string,
    content: { plainText: string; html: string },
  ) => void;
  /**
   * The block types the `/` slash menu can convert the current block into
   * (the page-document palette). Absent → no slash menu.
   */
  slashCommandTypes?: SlashCommandType[];
  /**
   * Convert a block to a different element type in place (the `/` slash menu).
   * The host swaps `typeId`, resets `data` to the target type's default, and
   * focuses the block. Absent in view/fill and in the form editor.
   */
  onElementConvert?: (elementId: string, targetTypeId: string) => void;
  /**
   * A caret arrow ran off the edge of a block. The host moves focus to the
   * adjacent text block: left → end of previous, right → start of next, up →
   * previous at `caretX` (last line), down → next at `caretX` (first line).
   */
  onElementArrow?: (
    elementId: string,
    direction: ArrowDirection,
    caretX?: number,
  ) => void;
  /**
   * Tab / Shift-Tab on a block: indent (+1) or un-indent (-1) its outline
   * depth (`data.indent`). The host enforces the depth rule (a block may sit
   * at most one level deeper than the block above it). `caret` is the
   * collapsed selection offset used to restore focus after the reproject.
   */
  onElementIndent?: (elementId: string, delta: 1 | -1, caret?: number) => void;
  /**
   * Remove a block from the document entirely (the entity card's "Remove from
   * page" action). The host deletes (or archives, if already published) the
   * element. Absent in view/fill and in the form editor.
   */
  onElementRemove?: (elementId: string) => void;
  /**
   * An image was pasted into a text block. The host uploads it and restructures
   * the document: an empty block becomes an image block; otherwise an image
   * block is inserted after (and, when the caret split text, a new block of the
   * same type carries the trailing `afterHtml`). Absent in view/fill/form editor.
   */
  onElementPasteImage?: (
    elementId: string,
    file: File,
    opts: { isEmpty: boolean; afterHtml?: string },
  ) => void;
  /**
   * Multi-block rich text was pasted into a text block. The host converts the
   * parsed descriptors into real page blocks: an empty block is replaced by
   * the first descriptor; otherwise the blocks are inserted after it (and,
   * when the caret split text, a trailing block carries `afterHtml`). Absent
   * in view/fill/form editor — pastes then coerce inline as before.
   */
  onElementPasteBlocks?: (
    elementId: string,
    blocks: PastedBlock[],
    opts: { isEmpty: boolean; afterHtml?: string },
  ) => void;
  /**
   * Full page blocks (the private clipboard MIME) were pasted into a text
   * block: lossless intra-app paste. The host clones the elements with FRESH
   * ids (preserving type/data/children) and inserts them like a block paste.
   * Absent in view/fill/form editor.
   */
  onElementPasteElements?: (
    elementId: string,
    elements: DocumentElement[],
    opts: { isEmpty: boolean; afterHtml?: string },
  ) => void;
  /**
   * Shift+ArrowUp/Down at the caret edge of a text block: the user is
   * extending selection PAST the block, i.e. entering multi-block selection.
   * The host starts a block selection at this element. Absent outside the
   * page-document editor.
   */
  onElementShiftArrow?: (elementId: string, direction: "up" | "down") => void;
  /**
   * Extra TipTap extensions the host injects into every page rich-text editor
   * (e.g. the app-specific `@` entity mention). Opaque to forms-ui-web.
   */
  richTextExtensions?: unknown[];
  /** Shared flag set by an injected suggestion popup while it's open. */
  richTextSuggestionActiveRef?: React.MutableRefObject<boolean>;
  /**
   * Live-editor registry for cross-block text selection: every mounted page
   * rich-text editor announces itself (editor handle, null on unmount) so the
   * host can hit-test drags (posAtCoords) and slice content across blocks.
   */
  registerRichTextEditor?: (elementId: string, editor: unknown | null) => void;
  /**
   * The active cross-block text selection (or null). Each text block paints
   * its own share of the range as selection-look decorations.
   */
  virtualTextSelection?: VirtualTextSelection | null;
  /**
   * The id of a freshly-inserted block that should grab focus. Its renderer
   * focuses itself once and calls `consumeAutoFocus`.
   */
  autoFocusElementId?: string | null;
  /**
   * Where to place the caret when auto-focusing: a character offset into the
   * block's text, or 'end' / undefined for the end. Used e.g. after a merge to
   * drop the caret at the join point.
   */
  autoFocusCaret?: number | 'end';
  /**
   * Coordinate-based caret placement for arrow (up/down) navigation — match a
   * screen x on the target's first or last line. Takes precedence over
   * `autoFocusCaret` when set.
   */
  autoFocusCoords?: AutoFocusCoords | null;
  consumeAutoFocus?: () => void;
}

const DocumentContext = createContext<DocumentContextValue | null>(null);

interface DocumentProviderProps {
  document: EacoDocumentContent;
  isEditor?: boolean;
  isReadOnly?: boolean;
  isPrint?: boolean;
  previewDefaults?: boolean;
  values: Record<string, unknown>;
  updateValues: Updater<Record<string, unknown>>;
  renderElementAnnotation?: (elementId: string) => React.ReactNode;
  updateElementData?: (
    elementId: string,
    updater: (data: Record<string, unknown>) => void,
  ) => void;
  onElementEnter?: (
    elementId: string,
    opts: { isEmpty: boolean; afterHtml?: string; atStart?: boolean },
  ) => void;
  onElementBackspaceStart?: (
    elementId: string,
    content: { plainText: string; html: string },
  ) => void;
  slashCommandTypes?: SlashCommandType[];
  onElementConvert?: (elementId: string, targetTypeId: string) => void;
  onElementArrow?: (
    elementId: string,
    direction: ArrowDirection,
    caretX?: number,
  ) => void;
  onElementIndent?: (elementId: string, delta: 1 | -1, caret?: number) => void;
  onElementRemove?: (elementId: string) => void;
  onElementPasteImage?: (
    elementId: string,
    file: File,
    opts: { isEmpty: boolean; afterHtml?: string },
  ) => void;
  onElementPasteBlocks?: (
    elementId: string,
    blocks: PastedBlock[],
    opts: { isEmpty: boolean; afterHtml?: string },
  ) => void;
  onElementPasteElements?: (
    elementId: string,
    elements: DocumentElement[],
    opts: { isEmpty: boolean; afterHtml?: string },
  ) => void;
  onElementShiftArrow?: (elementId: string, direction: "up" | "down") => void;
  richTextExtensions?: unknown[];
  richTextSuggestionActiveRef?: React.MutableRefObject<boolean>;
  registerRichTextEditor?: (elementId: string, editor: unknown | null) => void;
  virtualTextSelection?: VirtualTextSelection | null;
  autoFocusElementId?: string | null;
  autoFocusCaret?: number | 'end';
  autoFocusCoords?: AutoFocusCoords | null;
  consumeAutoFocus?: () => void;
  children: React.ReactNode;
}

export const DocumentProvider: React.FC<DocumentProviderProps> = ({
  document,
  isEditor = false,
  isReadOnly = false,
  isPrint = false,
  previewDefaults = false,
  values,
  updateValues,
  renderElementAnnotation,
  updateElementData,
  onElementEnter,
  onElementBackspaceStart,
  slashCommandTypes,
  onElementConvert,
  onElementArrow,
  onElementIndent,
  onElementRemove,
  onElementPasteImage,
  onElementPasteBlocks,
  onElementPasteElements,
  onElementShiftArrow,
  richTextExtensions,
  richTextSuggestionActiveRef,
  registerRichTextEditor,
  virtualTextSelection,
  autoFocusElementId,
  autoFocusCaret,
  autoFocusCoords,
  consumeAutoFocus,
  children,
}) => {
  const getAtPath = useCallback(
    (path: ValuePath) => valueAtPath(values, path),
    [values],
  );

  const setAtPath = useCallback(
    (path: ValuePath, value: unknown) => {
      // Build the next shape and return it from the updater. Immer's
      // standard "replace draft" pattern — works whether the underlying
      // draft is an object or an array (so we don't trip the
      // "array indices and length" invariant when an element's data was
      // ever stored as an array).
      updateValues(
        (draft) =>
          setValueAtPath(
            draft as Record<string, unknown>,
            path,
            value,
          ) as Record<string, unknown>,
      );
    },
    [updateValues],
  );

  const removeAtPathCallback = useCallback(
    (path: ValuePath) => {
      updateValues(
        (draft) =>
          removeAtPath(draft as Record<string, unknown>, path) as Record<
            string,
            unknown
          >,
      );
    },
    [updateValues],
  );

  return (
    <DocumentContext.Provider
      value={{
        document,
        isEditor,
        isReadOnly,
        isPrint,
        previewDefaults,
        values,
        updateValues,
        getAtPath,
        setAtPath,
        removeAtPath: removeAtPathCallback,
        renderElementAnnotation,
        updateElementData,
        onElementEnter,
        onElementBackspaceStart,
        slashCommandTypes,
        onElementConvert,
        onElementArrow,
        onElementIndent,
        onElementRemove,
        onElementPasteImage,
        onElementPasteBlocks,
        onElementPasteElements,
        onElementShiftArrow,
        richTextExtensions,
        richTextSuggestionActiveRef,
        registerRichTextEditor,
        virtualTextSelection,
        autoFocusElementId,
        autoFocusCaret,
        autoFocusCoords,
        consumeAutoFocus,
      }}
    >
      <RootResolvedStateProvider>{children}</RootResolvedStateProvider>
    </DocumentContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Repeat scope: threads the current path through the React tree so leaf
// field components can resolve to the right slice of the values object.
// ---------------------------------------------------------------------------

const RepeatScopeContext = createContext<ValuePath>([]);

interface RepeatScopeProviderProps {
  /** The repeatable section's elementId */
  sectionId: string;
  /** This instance's index within the section's array */
  index: number;
  children: React.ReactNode;
}

/**
 * Wraps a single instance of a repeatable section. Extends the parent scope's
 * path with `[sectionId, index]`, then provides a per-instance resolvedState
 * map computed against the merged outer + instance values.
 */
export const RepeatScopeProvider: React.FC<RepeatScopeProviderProps> = ({
  sectionId,
  index,
  children,
}) => {
  const parentPath = useContext(RepeatScopeContext);
  const path = useMemo(
    () => [...parentPath, sectionId, index],
    [parentPath, sectionId, index],
  );
  return (
    <RepeatScopeContext.Provider value={path}>
      <ResolvedStateProvider path={path}>{children}</ResolvedStateProvider>
    </RepeatScopeContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Resolved-state context: separated from DocumentContext so each scope
// (root, plus each repeat instance) has its own memoised map.
// ---------------------------------------------------------------------------

interface ResolvedStateValue {
  resolvedState: Map<string, ResolvedElementState>;
}

const ResolvedStateContext = createContext<ResolvedStateValue>({
  resolvedState: new Map(),
});

const RootResolvedStateProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { document, isEditor, values } = useDocument();
  const resolvedState = useMemo(() => {
    if (isEditor) return new Map<string, ResolvedElementState>();
    return resolveLogic(document.logic as LogicRule[] | undefined, values);
  }, [isEditor, document.logic, values]);
  return (
    <ResolvedStateContext.Provider value={{ resolvedState }}>
      {children}
    </ResolvedStateContext.Provider>
  );
};

interface ScopedResolvedStateProviderProps {
  path: ValuePath;
  children: React.ReactNode;
}

const ResolvedStateProvider: React.FC<ScopedResolvedStateProviderProps> = ({
  path,
  children,
}) => {
  const { document, isEditor, values } = useDocument();
  const resolvedState = useMemo(() => {
    if (isEditor) return new Map<string, ResolvedElementState>();
    // Merge outer values with the instance's slice. Instance values shadow
    // any same-named field in the outer scope (rare but defined).
    const instanceValues =
      (valueAtPath(values, path) as Record<string, unknown> | undefined) || {};
    const merged: Record<string, unknown> = { ...values, ...instanceValues };
    return resolveLogic(document.logic as LogicRule[] | undefined, merged);
    // path is stable per scope; values-at-path identity changes when the
    // instance's values change.
  }, [isEditor, document.logic, values, path]);
  return (
    <ResolvedStateContext.Provider value={{ resolvedState }}>
      {children}
    </ResolvedStateContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Public hooks
// ---------------------------------------------------------------------------

export const useDocument = () => {
  const context = useContext(DocumentContext);
  if (!context) {
    throw new Error("useDocument must be used within a DocumentProvider");
  }
  return context;
};

/** Returns the current repeat-scope path (empty at root). */
export const useRepeatScope = (): ValuePath => useContext(RepeatScopeContext);

export const useDocumentData = () => {
  const { values, updateValues } = useDocument();
  return { values, updateValues };
};

export const useIsEditor = () => {
  const { isEditor } = useDocument();
  return isEditor;
};

/**
 * Authoring write-path for self-editing blocks (page documents). Returns a
 * setter that mutates this element's own `data` in the document content, or
 * `null` when the host doesn't support inline editing (view / fill / a form
 * editor that hasn't opted in). Blocks should render read-only when null.
 */
export const useUpdateElementData = (elementId: string) => {
  const { updateElementData } = useDocument();
  return useMemo(() => {
    if (!updateElementData) return null;
    return (updater: (data: Record<string, unknown>) => void) =>
      updateElementData(elementId, updater);
  }, [updateElementData, elementId]);
};

/**
 * Notion-style Enter handling for a self-editing block. Returns a callback the
 * block invokes when Enter is pressed, or `null` when the host doesn't support
 * it (forms / view). `isEmpty` lets the host convert an empty block to a spacer.
 */
export const useElementEnter = (elementId: string) => {
  const { onElementEnter } = useDocument();
  return useMemo(() => {
    if (!onElementEnter) return null;
    return (opts: { isEmpty: boolean; afterHtml?: string; atStart?: boolean }) =>
      onElementEnter(elementId, opts);
  }, [onElementEnter, elementId]);
};

/**
 * Notion-style Backspace-at-start for a text-based block. Returns a callback
 * (passing the block's current content) or `null` when unsupported.
 */
export const useElementBackspaceStart = (elementId: string) => {
  const { onElementBackspaceStart } = useDocument();
  return useMemo(() => {
    if (!onElementBackspaceStart) return null;
    return (content: { plainText: string; html: string }) =>
      onElementBackspaceStart(elementId, content);
  }, [onElementBackspaceStart, elementId]);
};

/**
 * Paste an image into this text block. Returns a callback the block calls with
 * the pasted `File` (plus whether the block was empty and any trailing HTML
 * after the caret), or `null` when the host doesn't support it (view / fill /
 * form editor).
 */
export const useElementPasteImage = (elementId: string) => {
  const { onElementPasteImage } = useDocument();
  return useMemo(() => {
    if (!onElementPasteImage) return null;
    return (file: File, opts: { isEmpty: boolean; afterHtml?: string }) =>
      onElementPasteImage(elementId, file, opts);
  }, [onElementPasteImage, elementId]);
};

/**
 * Paste multi-block rich text into this text block. Returns a callback the
 * block calls with the parsed block descriptors (plus whether the block was
 * empty and any trailing HTML after the caret), or `null` when the host
 * doesn't support it (view / fill / form editor).
 */
export const useElementPasteBlocks = (elementId: string) => {
  const { onElementPasteBlocks } = useDocument();
  return useMemo(() => {
    if (!onElementPasteBlocks) return null;
    return (blocks: PastedBlock[], opts: { isEmpty: boolean; afterHtml?: string }) =>
      onElementPasteBlocks(elementId, blocks, opts);
  }, [onElementPasteBlocks, elementId]);
};

/**
 * Paste full page blocks (the private clipboard MIME) into this text block.
 * Returns a callback the block calls with the decoded `DocumentElement[]`
 * (plus whether the block was empty and any trailing HTML after the caret),
 * or `null` when the host doesn't support it.
 */
export const useElementPasteElements = (elementId: string) => {
  const { onElementPasteElements } = useDocument();
  return useMemo(() => {
    if (!onElementPasteElements) return null;
    return (
      elements: DocumentElement[],
      opts: { isEmpty: boolean; afterHtml?: string },
    ) => onElementPasteElements(elementId, elements, opts);
  }, [onElementPasteElements, elementId]);
};

/**
 * Shift+ArrowUp/Down ran off the caret edge of this text block — the user is
 * entering block selection. Returns a callback with the direction, or `null`
 * when the host doesn't support multi-block selection.
 */
export const useElementShiftArrow = (elementId: string) => {
  const { onElementShiftArrow } = useDocument();
  return useMemo(() => {
    if (!onElementShiftArrow) return null;
    return (direction: "up" | "down") =>
      onElementShiftArrow(elementId, direction);
  }, [onElementShiftArrow, elementId]);
};

/**
 * Remove this block from the document (e.g. the entity card's "Remove from
 * page" action). Returns a callback, or `null` when the host doesn't support
 * removal (view / fill).
 */
export const useRemoveElement = (elementId: string) => {
  const { onElementRemove } = useDocument();
  return useMemo(() => {
    if (!onElementRemove) return null;
    return () => onElementRemove(elementId);
  }, [onElementRemove, elementId]);
};

/**
 * The block types the `/` slash menu offers (page-document palette), or
 * undefined when the host provides no slash menu.
 */
export const useSlashCommandTypes = (): SlashCommandType[] | undefined => {
  const { slashCommandTypes } = useDocument();
  return slashCommandTypes;
};

/** Host-injected extra TipTap extensions for page rich-text editors. */
export const useRichTextExtensions = (): unknown[] | undefined => {
  const { richTextExtensions } = useDocument();
  return richTextExtensions;
};

/** Shared "a suggestion popup is open" flag for page rich-text editors. */
export const useRichTextSuggestionActiveRef = ():
  | React.MutableRefObject<boolean>
  | undefined => {
  const { richTextSuggestionActiveRef } = useDocument();
  return richTextSuggestionActiveRef;
};

/**
 * Register this block's live rich-text editor with the host (cross-block
 * selection hit-testing/slicing). Returns a callback to hand to PageRichText's
 * `onEditorHandle`, or undefined when the host doesn't track editors.
 */
export const useRegisterRichTextEditor = (elementId: string) => {
  const { registerRichTextEditor } = useDocument();
  return useMemo(() => {
    if (!registerRichTextEditor) return undefined;
    return (editor: unknown | null) =>
      registerRichTextEditor(elementId, editor);
  }, [registerRichTextEditor, elementId]);
};

/**
 * This block's share of the active cross-block text selection: the range of
 * its own doc to paint with selection-look decorations, or null. 'start'/'end'
 * mean the doc edges (the editor clamps to its actual size).
 */
export const useElementVirtualSelection = (
  elementId: string,
): { from: number | "start"; to: number | "end" } | null => {
  const { virtualTextSelection: v } = useDocument();
  return useMemo(() => {
    if (!v) return null;
    if (v.startId === elementId && v.endId === elementId) {
      return { from: v.startPos, to: v.endPos };
    }
    if (v.startId === elementId) return { from: v.startPos, to: "end" };
    if (v.endId === elementId) return { from: "start", to: v.endPos };
    if (v.coveredIds.includes(elementId)) return { from: "start", to: "end" };
    return null;
  }, [v, elementId]);
};

/**
 * Convert-in-place callback for the `/` slash menu, or `null` when unsupported.
 */
export const useElementConvert = (elementId: string) => {
  const { onElementConvert } = useDocument();
  return useMemo(() => {
    if (!onElementConvert) return null;
    return (targetTypeId: string) => onElementConvert(elementId, targetTypeId);
  }, [onElementConvert, elementId]);
};

/**
 * Caret arrow ran off the edge of this block. Returns a callback the block
 * invokes with the direction (+ the caret's screen x for up/down), or `null`
 * when the host doesn't support cross-block navigation.
 */
export const useElementArrow = (elementId: string) => {
  const { onElementArrow } = useDocument();
  return useMemo(() => {
    if (!onElementArrow) return null;
    return (direction: ArrowDirection, caretX?: number) =>
      onElementArrow(elementId, direction, caretX);
  }, [onElementArrow, elementId]);
};

/**
 * Tab / Shift-Tab outline indenting for this block, or null when the host
 * doesn't support it (only the page-document editor does).
 */
export const useElementIndent = (elementId: string) => {
  const { onElementIndent } = useDocument();
  return useMemo(() => {
    if (!onElementIndent) return null;
    return (delta: 1 | -1, caret?: number) =>
      onElementIndent(elementId, delta, caret);
  }, [onElementIndent, elementId]);
};

/**
 * Whether this block was just inserted and should grab focus. Returns a
 * `shouldFocus` flag plus a `consume` fn to clear it after focusing (once).
 * `coords` (when set) requests coordinate-based placement for arrow up/down.
 */
export const useAutoFocus = (elementId: string) => {
  const {
    autoFocusElementId,
    autoFocusCaret,
    autoFocusCoords,
    consumeAutoFocus,
  } = useDocument();
  const shouldFocus = autoFocusElementId === elementId;
  return {
    shouldFocus,
    // Where to drop the caret: a char offset, or 'end' (default).
    caret: shouldFocus ? autoFocusCaret ?? 'end' : ('end' as const),
    // Coordinate placement (arrow up/down), or null.
    coords: shouldFocus ? autoFocusCoords ?? null : null,
    consume: consumeAutoFocus ?? (() => {}),
  };
};

export const useIsReadOnly = () => {
  const { isReadOnly } = useDocument();
  return isReadOnly;
};

export const useIsPrint = () => {
  const { isPrint } = useDocument();
  return isPrint;
};

export const usePreviewDefaults = () => {
  const { previewDefaults } = useDocument();
  return previewDefaults;
};

/**
 * The IANA zone a bound output's instants are shown in (stamped by
 * renderFormOutput: the filler's zone, else the form owner's). Null for
 * unbound documents and on-screen previews, where the runtime's zone applies.
 */
export const useOutputTimeZone = (): string | null => {
  const { document } = useDocument();
  const binding = (document as { __binding?: { timeZone?: string | null } }).__binding;
  return binding?.timeZone ?? null;
};

/** The optional per-element annotation renderer (undefined when unset). */
export const useElementAnnotation = () => {
  const { renderElementAnnotation } = useDocument();
  return renderElementAnnotation;
};

/** Resolved logic state for a specific element (undefined if no rule matched). */
export const useResolvedElementState = (
  elementId: string,
): ResolvedElementState | undefined => {
  const { resolvedState } = useContext(ResolvedStateContext);
  return resolvedState.get(elementId);
};

/**
 * Every element's resolved logic state in the current scope, for decisions
 * that look at more than the element's own id (a hoisted page break follows
 * the containers it was moved out of).
 */
export const useResolvedStateMap = (): ReadonlyMap<string, ResolvedElementState> =>
  useContext(ResolvedStateContext).resolvedState;

/**
 * Hook to get and set a specific element's value. Scope-aware: if rendered
 * inside a RepeatScopeProvider, reads/writes the value from the current
 * instance's slice.
 *
 * Filler answers ride a uniform `{ value }` envelope so future per-answer
 * metadata (flagged, source, …) can land without re-shaping the responses
 * payload. The envelope only wraps the *answer* slot, not the element
 * configuration slot: inside a configForm (detected via the ConfigForm
 * subject context) the host element's `data` map should stay flat so
 * downstream consumers like `element.data.responseSetId` keep reading as
 * plain primitives.
 */
export const useElementValue = <T = unknown,>(elementId: string) => {
  const { getAtPath, setAtPath } = useDocument();
  const scope = useRepeatScope();
  const configFormSubject = useConfigFormSubject();
  const useEnvelope = configFormSubject === null;

  const path = useMemo(() => [...scope, elementId], [scope, elementId]);

  const raw = getAtPath(path);
  let value: T | undefined;
  // Single-key `{ value: ... }` envelopes are the canonical answer
  // shape; both contexts unwrap on read. The difference is the WRITE
  // side: filler context writes envelopes, configForm context writes
  // bare values so element.data stays primitive-shaped for downstream
  // consumers (e.g. `data.responseSetId.startsWith(...)`).
  const isEnvelope =
    raw !== null &&
    typeof raw === "object" &&
    !Array.isArray(raw) &&
    "value" in (raw as Record<string, unknown>) &&
    Object.keys(raw as Record<string, unknown>).length === 1;
  if (isEnvelope) {
    value = (raw as { value: T }).value;
  } else if (raw !== undefined) {
    value = raw as T;
  }
  void useEnvelope;

  const setElementValue = useCallback(
    (newValue: T) => {
      setAtPath(path, useEnvelope ? { value: newValue } : newValue);
    },
    [path, setAtPath, useEnvelope],
  );

  return [value, setElementValue] as const;
};

export default DocumentContext;
